import type React from "react";
import { Component } from "react";

import { Honeybadger, HoneybadgerErrorBoundary } from "@honeybadger-io/react";
import * as Sentry from "@sentry/react";
import type { AxiosError } from "axios";
import axios from "axios";
import { ZodError } from "zod";

import { isStage } from "utils/misc/getBuildEnvironment";
import { isTestRunning } from "utils/misc/isTestRunning";
import { getAppDataRegion } from "utils/region";

type AllowedErrorTypes = Error | AxiosError | ZodError | string;

export function isErrorOfType(error: unknown): error is AllowedErrorTypes {
  return error instanceof Error || axios.isAxiosError(error) || error instanceof ZodError || typeof error === "string";
}

const MAX_ISSUES_IN_MESSAGE = 5;

type ZodIssues = ZodError["issues"];

const formatIssuePath = (path: ReadonlyArray<PropertyKey>): string =>
  path.length === 0 ? "<root>" : path.map(String).join(".");

const joinWithOverflow = (lines: string[], total: number, separator: string): string => {
  const visible = lines.slice(0, MAX_ISSUES_IN_MESSAGE);
  if (total > MAX_ISSUES_IN_MESSAGE) {
    visible.push(`(+${total - MAX_ISSUES_IN_MESSAGE} more)`);
  }
  return visible.join(separator);
};

const formatZodIssues = (issues: ZodIssues): string =>
  joinWithOverflow(
    issues.map(issue => `• ${formatIssuePath(issue.path)}: ${issue.message}`),
    issues.length,
    "\n"
  );

function getErrorMessage(error: AllowedErrorTypes) {
  if (axios.isAxiosError(error)) {
    return JSON.stringify(error.response?.data);
  }

  if (error instanceof ZodError) {
    return formatZodIssues(error.issues);
  }

  return "";
}

function getTags(error: AllowedErrorTypes) {
  if (axios.isAxiosError(error)) {
    return [
      "http",
      `status:${error?.response?.status}`,
      `method:${error?.config?.method}`,
      `url:${error?.config?.url}`,
    ];
  }

  if (error instanceof ZodError) {
    return ["validation"];
  }

  return [];
}

export const reportError = (
  name: string,
  error: AllowedErrorTypes,
  options?: { action?: string; message?: string; context?: Record<string, unknown> }
): void => {
  if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "test") {
    if (error instanceof ZodError) {
      // eslint-disable-next-line no-console
      console.error(name, formatZodIssues(error.issues), error.issues);
    } else {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  if (process.env.NODE_ENV !== "development" && process.env.NODE_ENV !== "test") {
    const context = {
      ...options?.context,
      ...(error instanceof ZodError ? { zodIssues: error.issues } : {}),
    };
    Honeybadger.notify(error, name, {
      action: options?.action,
      context,
      message: options?.message || getErrorMessage(error),
      tags: getTags(error),
    });

    // Report to Sentry as well while we evaluate the two error trackers.
    // No-op when Sentry is not initialized (missing DSN, localhost, tests).
    Sentry.withScope(scope => {
      scope.setTag("report_name", name);
      if (options?.action) {
        scope.setTag("action", options.action);
      }
      scope.setContext("report", {
        name,
        message: options?.message || getErrorMessage(error),
        ...context,
      });

      if (typeof error === "string") {
        Sentry.captureMessage(`${name}: ${error}`, "error");
      } else {
        Sentry.captureException(error);
      }
    });
  }
};

interface Props {
  children: React.ReactNode;
}

/**
 * Reports render errors to Sentry and rethrows them, so the surrounding
 * HoneybadgerErrorBoundary still catches the error and renders its fallback.
 * Both trackers receive the error while we evaluate them side by side.
 */
class SentryRenderErrorReporter extends Component<Props> {
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureReactException(error, errorInfo, { mechanism: { handled: false } });

    // Rethrow during the next render so the error propagates to the parent
    // (Honeybadger) boundary. https://react.dev/reference/react/Component#componentdidcatch
    this.setState(() => {
      throw error;
    });
  }

  render() {
    const { children } = this.props;
    return children;
  }
}

export const CustomErrorBoundary: React.FC<Props> = ({ children }) => {
  if (isTestRunning() || window.location.hostname.includes("localhost")) {
    return <>{children}</>;
  }

  // FIXME: honeybadger should probably be initiated when module is loading. Preferably with env variables or some static config.
  const honeybadger = Honeybadger.configure({
    apiKey: "183c57a3",
    environment: isStage() ? "staging" : "production",
    enableUnhandledRejection: false,
  });

  honeybadger.beforeNotify(notice => {
    if (notice?.message && /ResizeObserver loop/.test(notice.message)) {
      return false;
    }
    return true;
  });

  honeybadger.setContext({
    market: getAppDataRegion(),
    release: `${process.env.REACT_APP_VERSION}-${getAppDataRegion()}`,
  });

  return (
    <HoneybadgerErrorBoundary honeybadger={honeybadger}>
      <SentryRenderErrorReporter>{children}</SentryRenderErrorReporter>
    </HoneybadgerErrorBoundary>
  );
};
